import React, { useContext, useState } from 'react';
import { Box, Button } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CircularProgress from '@mui/material/CircularProgress';
import PropTypes from 'prop-types';
import useStyles from './styles';
import { steps, FundType } from '../constants';
import { PlanContext } from '../context/PlanContext';
import PlanningButton from './common/PlanningButton';
import getCurrentDateInISOWithTimezone from '../../../../shared/helpers/getCurrentDateInISOWithTimeZone';

const Footer = ({
  isMobile = false,
  currentStep,
  setCurrentStep,
  setFurthestStep,
  isNextDisabled = false,
  setNextDisabled,
  onComplete,
  onReturnToInvestmentSnapshot = null,
}) => {
  const classes = useStyles();
  const hasPrevious = currentStep > 0 || Boolean(onReturnToInvestmentSnapshot);
  const [isPreviousLoading, setIsPreviousLoading] = useState(false);

  const {
    plan,
    updatedPlan,
    setUpdatedPlan,
    savePlan,
    planningButtonLoading,
    setPlanningButtonLoading,
  } = useContext(PlanContext);

  const getAllocations = (targetPlan = updatedPlan) => {
    const coreFundAllocation = targetPlan?.fundTypeAllocations?.find(
      (fund) => fund.fundType === FundType.CORE,
    )?.amount || 0;
    const focusedFundAllocation = targetPlan?.fundTypeAllocations?.find(
      (fund) => fund.fundType === FundType.FOCUSED,
    )?.amount || 0;
    const followOnFundAllocation = targetPlan?.fundTypeAllocations?.find(
      (fund) => fund.fundType === FundType.FOLLOW_ON,
    )?.amount || 0;
    const syndicationFundAllocation = targetPlan?.fundTypeAllocations?.find(
      (fund) => fund.fundType === FundType.SYNDICATION,
    )?.amount || 0;
    return {
      coreFundAllocation,
      focusedFundAllocation,
      followOnFundAllocation,
      syndicationFundAllocation,
    };
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
    });
  };

  const onNext = async () => {
    setPlanningButtonLoading(true);
    try {
      const previousAllocations = getAllocations(plan);
      const nextAllocations = getAllocations(updatedPlan);
      const budgetChanged = currentStep === steps.indexOf('AllocateBudget')
        && Object.keys(nextAllocations).some(
          (key) => parseFloat(previousAllocations[key] || 0)
            !== parseFloat(nextAllocations[key] || 0),
        );

      if (
        parseInt(currentStep, 10) !== steps.indexOf('ReviewPlan')
        && updatedPlan
      ) {
        await savePlan(updatedPlan);
      }
      let nextStep = currentStep + 1;

      const {
        coreFundAllocation,
        focusedFundAllocation,
        followOnFundAllocation,
        syndicationFundAllocation,
      } = getAllocations();

      if (
        nextStep === steps.indexOf('CoreFundPlan')
        && !parseFloat(coreFundAllocation)
      ) {
        nextStep += 1; // Skip CoreFundPlan
      }

      if (
        nextStep === steps.indexOf('FocusedFundPlan')
        && !parseFloat(focusedFundAllocation)
      ) {
        nextStep += 1; // Skip FocusedFundPlan
      }

      if (
        nextStep === steps.indexOf('FollowOnFundPlan')
        && !parseFloat(followOnFundAllocation)
      ) {
        nextStep += 1; // Skip FollowOnFundPlan
      }

      if (
        nextStep === steps.indexOf('SyndicationsPlan')
        && !parseFloat(syndicationFundAllocation)
      ) {
        nextStep += 1; // Skip SyndicationsPlan
      }

      const destinationStep = nextStep < steps.length
        ? nextStep
        : currentStep;
      setNextDisabled(true);
      setCurrentStep(destinationStep);
      setFurthestStep((previousFurthestStep) => (budgetChanged
        ? destinationStep
        : Math.max(previousFurthestStep, destinationStep)));
      setPlanningButtonLoading(false);
      scrollToTop();
    } catch (e) {
      console.error('Error while saving plan');
      setPlanningButtonLoading(false);
    }
  };

  const onPrevious = () => {
    setIsPreviousLoading(true);
    setNextDisabled(true);

    if (currentStep === 0 && onReturnToInvestmentSnapshot) {
      setUpdatedPlan(plan);
      onReturnToInvestmentSnapshot();
      setIsPreviousLoading(false);
      scrollToTop();
      return;
    }

    let previousStep = currentStep - 1;

    const {
      coreFundAllocation,
      focusedFundAllocation,
      followOnFundAllocation,
      syndicationFundAllocation,
    } = getAllocations();

    if (
      previousStep === steps.indexOf('SyndicationsPlan')
      && !parseFloat(syndicationFundAllocation)
    ) {
      previousStep -= 1;
    }

    if (
      previousStep === steps.indexOf('FollowOnFundPlan')
      && !parseFloat(followOnFundAllocation)
    ) {
      previousStep -= 1;
    }

    if (
      previousStep === steps.indexOf('FocusedFundPlan')
      && !parseFloat(focusedFundAllocation)
    ) {
      previousStep -= 1;
    }

    if (
      previousStep === steps.indexOf('CoreFundPlan')
      && !parseFloat(coreFundAllocation)
    ) {
      previousStep -= 1;
    }

    setCurrentStep(previousStep >= 0 ? previousStep : currentStep);
    // reset to orignal plan
    setUpdatedPlan(plan);
    setIsPreviousLoading(false);
    scrollToTop();
  };

  const onSubmit = async () => {
    setPlanningButtonLoading(true);
    try {
      await savePlan({
        ...updatedPlan,
        investmentPlanCompletionDate: getCurrentDateInISOWithTimezone(),
      });
      onComplete();
    } catch (error) {
      console.error('Error saving plan:', error);
    } finally {
      setPlanningButtonLoading(false);
      scrollToTop();
    }
  };

  return (
    <>
      <Box
        className={classes.footer}
        style={{
          justifyContent: hasPrevious ? 'space-between' : 'flex-end',
          ...(isMobile && { width: '100%', left: 0 }),
        }}
      >
        {hasPrevious && (
          <Button
            className={classes.navigationButton}
            variant="text"
            onClick={onPrevious}
            disabled={isPreviousLoading}
          >
            {isPreviousLoading ? (
              <CircularProgress color="inherit" size="25px" />
            ) : (
              <ChevronLeftIcon />
            )}
            Previous
          </Button>
        )}
        {currentStep === (steps.length - 1) ? (
          <PlanningButton
            variant="text"
            text="Submit"
            onClick={onSubmit}
            disabled={isNextDisabled || planningButtonLoading}
            isLoading={planningButtonLoading}
          />
        ) : (
          <PlanningButton
            variant="text"
            text="Next"
            onClick={onNext}
            disabled={isNextDisabled || planningButtonLoading}
            isLoading={planningButtonLoading}
          />
        )}
      </Box>
    </>
  );
};

Footer.propTypes = {
  isMobile: PropTypes.bool,
  currentStep: PropTypes.number.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  setFurthestStep: PropTypes.func.isRequired,
  isNextDisabled: PropTypes.bool,
  setNextDisabled: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onReturnToInvestmentSnapshot: PropTypes.func,
};

Footer.defaultProps = {
  isMobile: false,
  isNextDisabled: false,
  onReturnToInvestmentSnapshot: null,
};

export default Footer;
